/**
 * Parsers pentru KML și GeoJSON — extrag poligoane cu coordonate lat/lng.
 * Returnează întotdeauna: [{ name, points: [{lat, lng}] }]
 */

/** Parse GeoJSON string/object -> array of {name, points} */
export function parseGeoJSON(input) {
  const obj = typeof input === "string" ? JSON.parse(input) : input;
  const polys = [];
  const walkGeom = (geom, name) => {
    if (!geom) return;
    if (geom.type === "Polygon") {
      // coordinates: [ [ [lng,lat], ... ], holes... ]
      const outer = geom.coordinates?.[0] || [];
      polys.push({
        name: name || `Poligon ${polys.length + 1}`,
        points: outer.filter(c => Array.isArray(c) && c.length >= 2)
          .map(([lng, lat]) => ({ lat, lng })),
      });
    } else if (geom.type === "MultiPolygon") {
      (geom.coordinates || []).forEach((poly, i) => {
        const outer = poly?.[0] || [];
        polys.push({
          name: name ? `${name} #${i + 1}` : `Poligon ${polys.length + 1}`,
          points: outer.filter(c => Array.isArray(c) && c.length >= 2)
            .map(([lng, lat]) => ({ lat, lng })),
        });
      });
    } else if (geom.type === "GeometryCollection") {
      (geom.geometries || []).forEach(g => walkGeom(g, name));
    }
  };
  if (obj.type === "FeatureCollection") {
    (obj.features || []).forEach((f) => {
      const nm = f.properties?.name || f.properties?.Name || null;
      walkGeom(f.geometry, nm);
    });
  } else if (obj.type === "Feature") {
    walkGeom(obj.geometry, obj.properties?.name || null);
  } else if (obj.type) {
    walkGeom(obj, null);
  }
  // Close polygons: remove trailing duplicate point (KML/GeoJSON require closed rings)
  return polys.map(p => {
    let pts = p.points;
    if (pts.length >= 2) {
      const first = pts[0];
      const last = pts[pts.length - 1];
      if (first.lat === last.lat && first.lng === last.lng) {
        pts = pts.slice(0, -1);
      }
    }
    return { ...p, points: pts };
  }).filter(p => p.points.length >= 3);
}

/** Parse KML text -> array of {name, points} */
export function parseKML(text) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "application/xml");
  const parseError = doc.querySelector("parsererror");
  if (parseError) throw new Error("KML invalid: " + parseError.textContent);
  const polys = [];
  // Placemark contains name + Polygon (or MultiGeometry)
  const placemarks = doc.getElementsByTagName("Placemark");
  Array.from(placemarks).forEach((pm) => {
    const nameEl = pm.getElementsByTagName("name")[0];
    const baseName = nameEl?.textContent?.trim() || `Poligon ${polys.length + 1}`;
    const polygonEls = pm.getElementsByTagName("Polygon");
    Array.from(polygonEls).forEach((pol, idx) => {
      // outerBoundaryIs > LinearRing > coordinates
      const outer = pol.getElementsByTagName("outerBoundaryIs")[0];
      const ring = outer?.getElementsByTagName("LinearRing")[0];
      const coordEl = ring?.getElementsByTagName("coordinates")[0];
      if (!coordEl) return;
      const raw = coordEl.textContent.trim();
      // Coordinates: "lng,lat,alt lng,lat,alt ..." (whitespace separated)
      const points = raw.split(/\s+/).map((tuple) => {
        const parts = tuple.split(",").map(Number);
        return { lng: parts[0], lat: parts[1] };
      }).filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
      // Drop closing duplicate
      let pts = points;
      if (pts.length >= 2) {
        const f = pts[0], l = pts[pts.length - 1];
        if (f.lat === l.lat && f.lng === l.lng) pts = pts.slice(0, -1);
      }
      if (pts.length >= 3) {
        polys.push({
          name: polygonEls.length > 1 ? `${baseName} #${idx + 1}` : baseName,
          points: pts,
        });
      }
    });
  });
  return polys;
}

/** Detect and parse either KML or GeoJSON from a File */
export async function parseGeoFile(file) {
  const text = await file.text();
  const lower = file.name.toLowerCase();
  if (lower.endsWith(".geojson") || lower.endsWith(".json")) {
    return parseGeoJSON(text);
  }
  if (lower.endsWith(".kml")) {
    return parseKML(text);
  }
  // Try to detect content
  const trimmed = text.trim();
  if (trimmed.startsWith("{")) return parseGeoJSON(text);
  if (trimmed.startsWith("<")) return parseKML(text);
  throw new Error("Format nerecunoscut (folosește .kml, .geojson sau .json)");
}
